import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./styles.module.scss";
import { AspectOptimization } from "@pages/CROModule/components/Product/Optimisation/components/DefaultOptimization/Aspects";
import { useProductStore } from "src/store/overviewProduct.state";
import { useIsMobile } from "src/utils/isMobile";
import { useIsOn } from "@hooks/useFeatureToggle";
import { FEATURE_TOGGLES } from "@lib/featureToggles";
import { AspectsService } from "@services/amazon/aspects/aspects.service";
import {
  AspectsStatsMock,
  AspectReviewsMock,
  AspectImproveMock,
  ProductProblemsMock,
  AspectsStatsCompareMock,
} from "./mock";
import { SpiderChart } from "./components/SpiderChart";
import { ProblemsSolution } from "./components/ProblemsSolution";
import { AspectInsight } from "./components/AspectInsight";
import { StatsType } from "./components/SpiderChart/types";
import { AspectImprove } from "./components/AspectImprove";
import { AspectAlert } from "./components/AspectAlert";
import { findMatchingAspects, getAsinsFromSelectedProducts } from "./utils";

interface ProblemsBlockProps {
  intelligence?: boolean;
  whatIfCompare?: boolean;
}

const ProblemsBlock: React.FC<ProblemsBlockProps> = ({
  intelligence,
  whatIfCompare = false,
}) => {
  const { selectedProduct, selectedProductToCompare } = useProductStore();
  const isMobile = useIsMobile();
  const dataFetchedRef = useRef(false);

  const [aspectsStats, setAspectsStats] = useState(AspectsStatsMock.items);
  const aspectReviews = AspectReviewsMock.items;
  const [productProblems, setProductProblems] = useState(
    ProductProblemsMock.items,
  );
  const aspectImprove = AspectImproveMock;

  const [activeIndex, setActiveIndex] = useState(0);
  const [currentAspectData, setCurrentAspectData] = useState<StatsType | null>(
    null,
  );

  const productSpiderComponentDashboardEnabled = useIsOn(
    FEATURE_TOGGLES.SPIDER_COMPONENT_DASHBOARD,
  );
  const productProblemsComponentDashboardEnabled = useIsOn(
    FEATURE_TOGGLES.PROBLEMS_COMPONENT_DASHBOARD,
  );
  const productSolutionsComponentDashboardEnabled = useIsOn(
    FEATURE_TOGGLES.SOLUTIONS_COMPONENT_DASHBOARD,
  );
  const productAspectImproveComponentDashboardEnabled = useIsOn(
    FEATURE_TOGGLES.ASPECT_IMPROVE_COMPONENT_DASHBOARD,
  );
  const productAspectInsightComponentDashboardEnabled = useIsOn(
    FEATURE_TOGGLES.ASPECT_INSIGHTS_COMPONENT_DASHBOARD,
  );

  useEffect(() => {
    if (aspectsStats.length > 0) {
      setCurrentAspectData(aspectsStats[activeIndex]);
    }
  }, [activeIndex, aspectsStats]);

  const fetchAspectsStats = async () => {
    try {
      const aspectsService = new AspectsService();
      const response = await aspectsService.fetchAspectsStats(
        "AMAZON",
        selectedProduct,
        0,
        10,
      );
      setAspectsStats(
        response?.items?.length > 0 ? response.items : AspectsStatsMock.items,
      );
    } catch (error) {
      console.error("Error fetching aspects stats:", error);
    }
  };

  const fetchProductProblems = async () => {
    try {
      const aspectsService = new AspectsService();
      const response = await aspectsService.fetchProductProblems(
        "AMAZON",
        JSON.stringify(selectedProduct),
        0,
        10,
      );
      setProductProblems(
        response && response.items.length > 0
          ? response.items
          : ProductProblemsMock.items,
      );
    } catch (error) {
      console.error("Error fetching product problems:", error);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      fetchAspectsStats();
      fetchProductProblems();
    }
  }, [selectedProduct]);

  const asins = useMemo(() => {
    return selectedProductToCompare.length > 0
      ? getAsinsFromSelectedProducts(selectedProductToCompare)
      : [];
  }, [selectedProductToCompare]);

  const fetchComparedAspectsStats = useCallback(async (asinArray: string[]) => {
    if (!asinArray.length) return [];
    const aspectsService = new AspectsService();
    const results = await Promise.all(
      asinArray.map((asin) =>
        aspectsService
          .fetchAspectsStats("AMAZON", asin, 0, 10)
          .then((res) => ({ asin, items: res?.items || [] }))
          .catch(() => ({ asin, items: [] })),
      ),
    );
    return results;
  }, []);

  const [compareAspectsData, setCompareAspectsData] = useState(null);
  const [matchedOriginalAspects, setMatchedOriginalAspects] = useState([]);

  useEffect(() => {
    if (asins.length === 0) {
      setCompareAspectsData(null);
      setMatchedOriginalAspects([]);
      return;
    }
    if (aspectsStats.length === 0) return;
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    fetchComparedAspectsStats(asins).then((allStats) => {
      const [matchedAspects, combinedCompare] = findMatchingAspects(
        aspectsStats,
        allStats,
      );
      setMatchedOriginalAspects(matchedAspects);
      setCompareAspectsData(combinedCompare);
    });

    return () => {
      dataFetchedRef.current = false;
    };
  }, [asins, aspectsStats, fetchComparedAspectsStats]);

  return (
    <div className={styles.expandedContainer}>
      <div className={styles.expandedContent}>
        <div className={styles.comparisonBetween}>
          {isMobile && (
            <div className={styles.aspects}>
              {aspectsStats.length > 0 ? (
                <AspectOptimization
                  AspectsStats={aspectsStats}
                  AspectsReviews={aspectReviews}
                  isWhatIf={true}
                />
              ) : (
                <div className={styles.emptyBlock}>empty</div>
              )}
            </div>
          )}

          <div className={styles.col}>
            {productSpiderComponentDashboardEnabled && (
              <SpiderChart
                AspectsStats={aspectsStats}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                whatIfCompare={whatIfCompare}
                matchedOriginalAspects={
                  selectedProductToCompare.length > 0
                    ? matchedOriginalAspects
                    : []
                }
                compareAspectsData={
                  selectedProductToCompare.length > 0 ? compareAspectsData : []
                }
              />
            )}

            {!intelligence && productProblemsComponentDashboardEnabled && (
              <ProblemsSolution
                type="problem"
                productProblems={productProblems}
                activeIndex={activeIndex}
              />
            )}
          </div>
          <div className={styles.col}>
            {productAspectInsightComponentDashboardEnabled && (
              <AspectInsight
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                currentAspectData={currentAspectData}
                AspectsReviews={aspectReviews}
              />
            )}
            {selectedProductToCompare.length > 1 && (
              <AspectAlert data={AspectsStatsCompareMock} />
            )}
            {productAspectImproveComponentDashboardEnabled && (
              <AspectImprove
                aspectImprove={aspectImprove}
                activeIndex={activeIndex}
              />
            )}
            {!intelligence && productSolutionsComponentDashboardEnabled && (
              <ProblemsSolution
                type="solution"
                productProblems={productProblems}
                activeIndex={activeIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProblemsBlock;
